import UserInformationForm, {
  Errors,
  initialErrors,
  State,
} from 'components/UserInformation/UserInformationForm';
import { GenderValues } from 'enums/Genders';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import User from 'types/User';
import privateAPI from 'utils/privateAxiosInstance';

const initialState: State = {
  gender: 'NOT_SPECIFIED' as GenderValues,
  firstName: '',
  lastName: '',
  email: '',
  organism: '',
  professionalSituation: '',
  priority: false,
  phone: '',
  address: '',
  postalCode: '',
  city: '',
  region: '',
  country: '',
  dateOfBirth: '',
  isMetropolitan: false,
  presentation: '',
  comments: '',
};

type Props = {
  mentee: User | undefined;
};
const EditInformationForm: FC<Props> = ({ mentee }) => {
  const { mutate } = useSWRConfig();
  const navigate = useNavigate();

  const [state, setState] = useState<State>(initialState);
  const [errors, setErrors] = useState<Errors>(initialErrors);
  const [loading, setLoading] = useState<boolean>(false);

  // Updates state when user data is loaded
  const refreshState = useCallback(() => {
    mentee &&
      setState({
        gender: mentee.gender || 'NOT_SPECIFIED',
        firstName: mentee.firstName || '',
        lastName: mentee.lastName || '',
        email: mentee.email?.replace(' ', '') || '',
        phone: mentee.phone || '',
        address: mentee.address || '',
        postalCode: mentee.postalCode || '',
        city: mentee.city || '',
        region: mentee.region || '',
        country: mentee.country || '',
        isMetropolitan: !!mentee.isMetropolitan,
        dateOfBirth: mentee.dateOfBirth
          ? new Date(mentee.dateOfBirth).toISOString().slice(0, 10)
          : '',
        organism: mentee.organism || '',
        professionalSituation: mentee.professionalSituation || '',
        priority: !!mentee.priority,
        presentation: mentee.presentation || '',
        comments: mentee.comments || '',
      });
  }, [mentee, setState]);

  useEffect(() => {
    refreshState();
  }, [refreshState]);

  const handleCancel = () => {
    refreshState();
    if (mentee) {
      navigate(`/admin/mentoring/mentees/${mentee.id}/`);
    }
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    if (mentee) {
      setLoading(true);
      setErrors(initialErrors);

      const newDateOfBirth = new Date(state.dateOfBirth);

      privateAPI
        .patch(`/user/${mentee.id}`, {
          ...state,
          // La situation actuelle ne concerne que les mentors : on ne l'envoie pas ici.
          professionalSituation: undefined,
          phone: state.phone || null,
          dateOfBirth:
            newDateOfBirth.toString() === 'Invalid Date'
              ? null
              : newDateOfBirth,
        })
        .then(() => {
          mutate(`/user/${mentee.id}`);
          setLoading(false);
          navigate(`/admin/mentoring/mentees/${mentee.id}/`);
        })
        .catch((err) => {
          setLoading(false);
          if (err?.response?.data?.fields?.includes('email')) {
            setErrors({
              ...initialErrors,
              email: true,
            });
          } else {
            setErrors({
              ...initialErrors,
              general: true,
            });
          }
          window.scrollTo({ top: 0 });
        });
    }
  };

  return (
    <section>
      <UserInformationForm
        {...{
          state,
          setState,
          errors,
          setErrors,
          loading,
        }}
        variant="young"
        adminView
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </section>
  );
};

export default EditInformationForm;
